import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  BarChart3, 
  ExternalLink, 
  FileText 
} from 'lucide-react';

const CompanyHeader = ({ company }) => {
  const getMetricLabel = (metricKey) => {
    // Custom metric label logic
    if (metricKey.startsWith('custom')) {
      const labelKey = `${metricKey}_label`;
      if (company.metrics[labelKey] && company.metrics[labelKey].trim() !== '') {
        return company.metrics[labelKey];
      }
      // fallback default
      return metricKey.replace('custom', 'Custom ');
    }
    const labels = {
      // Idea Stage
      marketSize: 'TAM',
      problemValidation: 'Validation',
      solutionFit: 'Market Fit',
      // Pre-Seed
      mvpUsers: 'Live Clients',
      userRetention: 'Retention',
      feedback: 'Feedback',
      partnerships: 'Strategic Partnerships',
      // Seed
      monthlyUsers: 'MAU',
      monthlyRevenue: 'MRR',
      growthRate: 'Growth',
      customerAcquisition: 'CAC',
      // Series A
      annualRevenue: 'ARR',
      customerCount: 'Customers',
      netRetention: 'Net Retention',
      burnRate: 'Burn Rate',
      runway: 'Runway',
      // Series B
      arr: 'ARR',
      enterpriseCustomers: 'Enterprise',
      international: 'Markets',
      teamSize: 'Team Size',
      marketShare: 'Market Share',
      // Legacy support
      seeking: 'Seeking',
      users: 'Users',
      growth: 'Growth'
    };
    return labels[metricKey] || metricKey.charAt(0).toUpperCase() + metricKey.slice(1);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 sm:mb-14 bg-white/95 backdrop-blur-lg rounded-3xl p-4 sm:p-10 shadow-2xl">
      {company.logoUrl ? (
        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
          <img 
            src={company.logoUrl} 
            alt={`${company.name} logo`}
            className="w-full h-full object-contain rounded-2xl"
          />
        </div>
      ) : (
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mx-auto mb-4 sm:mb-6 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
          {company.logo}
        </div>
      )}
      <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 sm:mb-4">
        {company.name}
      </h1>
      <p className="text-base sm:text-xl text-gray-600 mb-4 sm:mb-6 max-w-2xl mx-auto">
        {company.tagline}
      </p>
      <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mb-4">
        {company.location && (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-xl text-blue-700 text-xs sm:text-sm">
            <Building2 className="w-4 h-4" /> {company.location}
          </span>
        )}
        {company.stage && (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-xl text-purple-700 text-xs sm:text-sm">
            <BarChart3 className="w-4 h-4" /> {company.stage.charAt(0).toUpperCase() + company.stage.slice(1)} Stage
          </span>
        )}
        {company.website && (
          <a href={company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 rounded-xl text-green-700 text-xs sm:text-sm hover:underline">
            <ExternalLink className="w-4 h-4" /> Website
          </a>
        )}
        {company.founded && (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-xl text-gray-700 text-xs sm:text-sm">
            <FileText className="w-4 h-4" /> Founded {company.founded}
          </span>
        )}
      </div>
      {/* Key Metrics */}
      {company.metrics && Object.keys(company.metrics).length > 0 && (
        <div className="mt-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Object.entries(company.metrics)
              .filter(([key, value]) => {
                // Hide the stage key
                if (key === 'stage') return false;
                // Hide label keys (e.g., custom1_label)
                if (key.endsWith('_label')) return false;
                // Hide empty values
                if (!value || value.trim() === '') return false;
                // For custom metrics, only show if label is set and non-empty
                if (key.startsWith('custom')) {
                  const labelKey = `${key}_label`;
                  if (!company.metrics[labelKey] || company.metrics[labelKey].trim() === '') {
                    return false;
                  }
                }
                return true;
              })
              .slice(0, 8) // Limit to 8 metrics for display
              .map(([key, value]) => (
                <div key={key} className="bg-white/70 rounded-2xl p-3 sm:p-4 text-center hover:bg-white/80 transition-all duration-200">
                  <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1">
                    {value}
                  </div>
                  <div className="text-gray-600 text-xs sm:text-sm font-medium">
                    {getMetricLabel(key)}
                  </div>
                </div>
              ))}
          </div>
          
          {/* Show more indicator if there are more than 8 metrics */}
          {Object.entries(company.metrics).filter(([key, value]) => key !== 'stage' && value && value.trim() !== '').length > 8 && (
            <div className="text-center mt-3">
              <span className="text-xs text-gray-500">
                +{Object.entries(company.metrics).filter(([key, value]) => key !== 'stage' && value && value.trim() !== '').length - 8} more metrics
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default CompanyHeader; 